import { useEffect } from "react";
import Layout from '../utils/core/Layout';
import './CookiesPolicy.css'; // or './PolicyStyles.css' if you renamed it

const TermsOfServicePage = () => {
  useEffect(() => {
    // scroll only when the page URL contains #top
    if (window.location.hash === "#top") {
      const heading = document.querySelector(".policy-page");
      if (heading) {
        heading.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, []);

  return (
    <Layout>
      <div className="policy-page">
        <h1 className="policy-title">Terms &amp; Conditions (T&amp;C)</h1>

        {/* Blue banner */}
        <div className="policy-banner">
          <p className="policy-banner-updated">
            <strong>Last Updated:</strong> 27/11/2025
          </p>
          <p className="policy-banner-text">
            PLEASE READ THESE TERMS &amp; CONDITIONS CAREFULLY BEFORE USING OUR PLATFORM
          </p>
        </div>

        <section className="policy-section">
          <p>
            Welcome to goneet (“we”, “our”, “us”). These Terms &amp; Conditions (“Terms”)
            govern your use of the goneet website, Android app, and iOS app (collectively, the
            “Platform”). By accessing or using the Platform, you agree to these Terms.
          </p>
        </section>

        {/* 1. Eligibility */}
        <section className="policy-section">
          <h2>1. Eligibility</h2>
          <p>You must be at least 18 years old to use the Platform.</p>
          <p>We do not knowingly allow minors to create accounts.</p>
        </section>

        {/* 2. Account Registration */}
        <section className="policy-section">
          <h2>2. Account Registration</h2>
          <p>To use our services, you must create an account with accurate information.</p>
          <p>You are responsible for:</p>
          <ul>
            <li>Maintaining account confidentiality</li>
            <li>All activities under your account</li>
            <li>Keeping your information updated</li>
          </ul>
          <p>We may suspend or terminate accounts that violate our policies.</p>
        </section>

        {/* 3. Professional Profile Information */}
        <section className="policy-section">
          <h2>3. Professional Profile Information</h2>
          <p>
            By creating a professional profile, you agree to provide true and accurate
            information about your:
          </p>
          <ul>
            <li>Work experience</li>
            <li>Job roles</li>
            <li>Skills</li>
            <li>Industry sectors</li>
            <li>Regions</li>
            <li>Employment history</li>
          </ul>
          <p>
            We may remove profiles that contain false, misleading, or inappropriate content.
          </p>
        </section>

        {/* 4. User Responsibilities */}
        <section className="policy-section">
          <h2>4. User Responsibilities</h2>
          <p>You agree NOT to:</p>
          <ul>
            <li>Post false information or fake experience</li>
            <li>Upload others’ photos without permission</li>
            <li>Attempt to hack, disrupt, or misuse the Platform</li>
            <li>Use the Platform for illegal, harmful, or abusive purposes</li>
          </ul>
        </section>

        {/* 5. Payments */}
        <section className="policy-section">
          <h2>5. Payments</h2>
          <p>Premium features or services may require payment.</p>

          <h3>A) Stripe (Website &amp; Android App)</h3>
          <p>
            Payments made through Stripe are subject to Stripe’s terms.
            We do not store your card details.
          </p>

          <h3>B) Apple In-App Purchases (iOS App)</h3>
          <p>
            Payments on iOS must be completed using Apple’s In-App Purchase system.
            All payment processing and refund rules are controlled by Apple.
          </p>
        </section>

        {/* 6. Premium Subscriptions */}
        <section className="policy-section">
          <h2>6. Premium Subscriptions</h2>
          <p>Premium plans (if applicable):</p>
          <ul>
            <li>Grant access to additional features</li>
            <li>Are billed according to the selected plan</li>
            <li>Automatically renew unless cancelled</li>
          </ul>
          <p>You may cancel your subscription at any time from:</p>
          <ul>
            <li>Stripe billing portal (web/Android)</li>
            <li>Apple ID subscription settings (iOS)</li>
          </ul>
        </section>

        {/* 7. Refunds */}
        <section className="policy-section">
          <h2>7. Refunds</h2>
          <p>
            Refunds are handled according to the Refund &amp; Cancellation Policy
            provided below.
          </p>
          <p>Apple users must request refunds directly from Apple Support.</p>
        </section>

        {/* 8. Content Ownership */}
        <section className="policy-section">
          <h2>8. Content Ownership</h2>
          <p>You retain ownership of the information you provide.</p>
          <p>
            By using the Platform, you grant us a non-exclusive license to use your data for:
          </p>
          <ul>
            <li>Operating the Platform</li>
            <li>Matching you with opportunities</li>
            <li>Displaying your profile</li>
            <li>Improving our services</li>
          </ul>
          <p>We do not sell your personal data.</p>
        </section>

        {/* 9. Intellectual Property */}
        <section className="policy-section">
          <h2>9. Intellectual Property</h2>
          <p>
            All text, logos, design, and content on goneet belong to us.
            You may not copy, reproduce, or distribute our content without permission.
          </p>
        </section>

        {/* 10. Termination */}
        <section className="policy-section">
          <h2>10. Termination</h2>
          <p>We may suspend or terminate your account if:</p>
          <ul>
            <li>You violate these Terms</li>
            <li>You provide false information</li>
            <li>You misuse the Platform</li>
            <li>It is required by law</li>
          </ul>
          <p>You may delete your account anytime.</p>
        </section>

        {/* 11. Limitation of Liability */}
        <section className="policy-section">
          <h2>11. Limitation of Liability</h2>
          <p>We are not responsible for:</p>
          <ul>
            <li>Any incorrect information added by users</li>
            <li>Hiring decisions made by employers</li>
            <li>Third-party actions</li>
            <li>Service interruptions</li>
            <li>Data loss caused by external factors</li>
          </ul>
          <p>You use the Platform at your own risk.</p>
        </section>

        {/* 12. Privacy */}
        <section className="policy-section">
          <h2>12. Privacy</h2>
          <p>
            Your use of the Platform is also governed by our Privacy Policy.
          </p>
        </section>

        {/* 13. Changes to Terms */}
        <section className="policy-section">
          <h2>13. Changes to Terms</h2>
          <p>
            We may update these Terms from time to time.
            Your continued use of the Platform means you accept the updated Terms.
          </p>
        </section>

        {/* 14. Contact Us */}
        <section className="policy-section">
          <h2>14. Contact Us</h2>
          <p>If you have questions regarding these Terms:</p>
          <p><strong>Email:</strong> support@goneet.com</p>
        </section>
      </div>
    </Layout>
  );
};

export default TermsOfServicePage;
